import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Product, ProductSearchPage, WindowRef } from '@spartacus/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GenMSRPPrice } from 'src/app/generac/models/product.model';
import { GenAutoShipQuantityChange } from 'src/app/generac/models/auto-ship.model';

@Component({
  selector: 'gen-auto-ship-product-scroll',
  templateUrl: './auto-ship-product-scroll.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GenAutoShipProductScrollComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() model: ProductSearchPage;
  @Input() netPrices: ReadonlyMap<string, GenMSRPPrice> = new Map();
  @Input() netPricesLoading = false;
  @Input() quantities: ReadonlyMap<string, number> = new Map();
  @Output() visible = new EventEmitter<void>();
  @Output() quantityChange = new EventEmitter<GenAutoShipQuantityChange>();

  hasBeenVisible = false;
  private readonly destroy$ = new Subject<void>();
  private readonly quantityControls = new Map<string, UntypedFormControl>();
  private observer?: IntersectionObserver;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private windowRef: WindowRef,
  ) { }

  ngAfterViewInit(): void {
    const IntersectionObserverRef = this.windowRef.nativeWindow && typeof IntersectionObserver !== 'undefined'
      ? IntersectionObserver
      : undefined;
    if (!IntersectionObserverRef) {
      this.markVisible();
      return;
    }

    this.observer = new IntersectionObserverRef((entries: IntersectionObserverEntry[]) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.markVisible();
      }
    }, { rootMargin: '200px 0px' });
    this.observer.observe(this.elementRef.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.quantities || changes.quantities.firstChange) {
      return;
    }

    this.quantityControls.forEach((control, productCode) => {
      control.setValue(this.quantities.get(productCode) ?? 0, { emitEvent: false });
      control.markAsPristine();
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByCode(_index: number, product: Product): string {
    return product.code ?? '';
  }

  getNetPrice(code?: string): GenMSRPPrice | undefined {
    return code ? this.netPrices.get(code) : undefined;
  }

  getQuantityControl(product: Product): UntypedFormControl {
    const code = product.code ?? '';
    let control = this.quantityControls.get(code);
    if (!control) {
      control = new UntypedFormControl(this.quantities.get(code) ?? 0);
      control.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((quantity) => this.quantityChange.emit({
          productCode: code,
          quantity: Number(quantity) || 0,
        }));
      this.quantityControls.set(code, control);
    }
    return control;
  }

  getAutoShipMinimum(product: Product): number {
    return Number(product.autoShipMinOrderQuantity) || this.getAutoShipStep(product);
  }

  getAutoShipStep(product: Product): number {
    return Number(product.autoShipAddToCartQtyMultiplier) || 1;
  }

  getAutoShipMaximum(product: Product): number {
    const maximum = Number(product.autoShipMaxOrderQuantity);
    return maximum > 0 ? maximum : Infinity;
  }

  private markVisible(): void {
    if (this.hasBeenVisible) {
      return;
    }
    this.hasBeenVisible = true;
    this.visible.emit();
    this.observer?.disconnect();
  }

}
